import axios from 'axios';
import { motion } from 'framer-motion';
import {
    Briefcase,
    Calendar,
    DollarSign,
    Edit,
    Eye,
    Filter,
    MapPin,
    Plus,
    Search,
    Trash2,
    Users
} from 'lucide-react';
import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Link } from 'react-router-dom';

const Jobs = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    status: 'all',
    location: '',
    jobType: 'all',
    sortBy: '-createdAt'
  });
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalJobs: 0,
    limit: 10
  });

  useEffect(() => {
    fetchJobs();
  }, [filters, pagination.currentPage, searchTerm]);

  const fetchJobs = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: pagination.currentPage,
        limit: pagination.limit,
        sortBy: filters.sortBy.replace('-', ''),
        sortOrder: filters.sortBy.startsWith('-') ? 'desc' : 'asc',
        ...(searchTerm && { search: searchTerm }),
        ...(filters.status !== 'all' && { status: filters.status }),
        ...(filters.location && { location: filters.location }),
        ...(filters.jobType !== 'all' && { jobType: filters.jobType })
      });

      console.log('📤 RECRUITER - Fetching jobs:', {
        url: `${process.env.REACT_APP_API_URL}/api/jobs/recruiter/my-jobs?${params}`,
        params: Object.fromEntries(params)
      });

      const token = localStorage.getItem('recruiter_token');
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/jobs/recruiter/my-jobs?${params}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      console.log('📥 RECRUITER - Jobs response:', {
        data: response.data,
        jobsArray: response.data?.data?.jobs,
        jobsLength: response.data?.data?.jobs?.length,
        totalPages: response.data?.data?.pagination?.totalPages,
        total: response.data?.data?.pagination?.totalJobs
      });

      // Handle the new API response structure
      const jobsData = response.data?.data?.jobs || [];
      const paginationData = response.data?.data?.pagination || {};
      const overallStats = response.data?.data?.overallStats || {};

      setJobs(jobsData);
      setPagination({
        ...pagination,
        totalPages: paginationData.totalPages || 1,
        totalJobs: paginationData.totalJobs || 0
      });

      // You can also use overallStats for additional UI elements if needed
      console.log('📊 RECRUITER - Overall stats:', overallStats);

    } catch (error) {
      console.error('❌ RECRUITER - Error fetching jobs:', {
        error: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      
      // Set empty array on error to prevent undefined issues
      setJobs([]);
      toast.error('Failed to fetch jobs');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (jobId) => {
    if (!window.confirm('Are you sure you want to delete this job? This action cannot be undone.')) {
      return;
    }

    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}/api/jobs/${jobId}`);
      toast.success('Job deleted successfully');
      fetchJobs();
    } catch (error) {
      console.error('Error deleting job:', error);
      toast.error('Failed to delete job');
    }
  };

  const handleStatusChange = async (jobId, newStatus) => {
    try {
      console.log('🔥 FRONTEND - Updating job status:', { jobId, newStatus });
      
      const token = localStorage.getItem('recruiter_token');
      const response = await axios.patch(
        `${process.env.REACT_APP_API_URL}/api/jobs/${jobId}`, 
        { status: newStatus },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      console.log('✅ FRONTEND - Status update response:', response.data);
      toast.success(`Job ${newStatus === 'Active' ? 'activated' : 'deactivated'} successfully`);
      fetchJobs();
    } catch (error) {
      console.error('❌ FRONTEND - Error updating job status:', {
        error: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.errors?.join(', ') || 
                          'Failed to update job status';
      toast.error(errorMessage);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters({ ...filters, [key]: value });
    setPagination({ ...pagination, currentPage: 1 });
  };

  const handlePageChange = (page) => {
    setPagination({ ...pagination, currentPage: page });
  };

  const getSalaryRange = (job) => {
    if (!job.salary) return 'Salary not specified';
    
    if (job.salary.min && job.salary.max) {
      return `$${job.salary.min.toLocaleString()} - $${job.salary.max.toLocaleString()}`;
    } else if (job.salary.min) {
      return `$${job.salary.min.toLocaleString()}+`;
    }
    return 'Salary not specified';
  };

  const getTimeAgo = (date) => {
    const now = new Date();
    const created = new Date(date);
    const diffInHours = Math.floor((now - created) / (1000 * 60 * 60));
    
    if (diffInHours < 24) {
      return `${diffInHours}h ago`;
    } else {
      const diffInDays = Math.floor(diffInHours / 24);
      return `${diffInDays}d ago`;
    }
  };

  if (loading && (!jobs || jobs.length === 0)) {
    return (
      <div className="p-6 max-w-screen-2xl mx-auto">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-8"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="h-64 bg-gray-200 rounded-xl"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-screen-2xl mx-auto">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col md:flex-row md:items-center md:justify-between mb-8"
      >
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Job Management</h1>
          <p className="text-gray-600">
            Manage your job postings and track their performance
          </p>
        </div>
        <Link
          to="/jobs/new"
          className="btn-primary flex items-center mt-4 md:mt-0"
        >
          <Plus className="w-5 h-5 mr-2" />
          Post New Job
        </Link>
      </motion.div>

      {/* Search and Filters */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="card mb-8"
      >
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Search */}
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search jobs by title, location, or description..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input-field pl-10"
              />
            </div>
          </div>

          {/* Filters */}
          <div className="flex flex-wrap gap-4">
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="input-field min-w-[120px]"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="draft">Draft</option>
            </select>

            <select
              value={filters.jobType}
              onChange={(e) => handleFilterChange('jobType', e.target.value)}
              className="input-field min-w-[120px]"
            >
              <option value="all">All Types</option>
              <option value="full-time">Full-time</option>
              <option value="part-time">Part-time</option>
              <option value="contract">Contract</option>
              <option value="freelance">Freelance</option>
              <option value="internship">Internship</option>
            </select>

            <select
              value={filters.sortBy}
              onChange={(e) => handleFilterChange('sortBy', e.target.value)}
              className="input-field min-w-[140px]"
            >
              <option value="-createdAt">Newest First</option>
              <option value="createdAt">Oldest First</option>
              <option value="title">Title A-Z</option>
              <option value="-title">Title Z-A</option>
              <option value="-views">Most Views</option>
              <option value="-applicationsCount">Most Applications</option>
            </select>
          </div>
        </div>

        {/* Results Summary */}
        <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-200">
          <p className="text-sm text-gray-600">
            Showing {jobs?.length || 0} of {pagination.totalJobs} jobs
          </p>
          <div className="flex items-center space-x-2">
            <Filter className="w-4 h-4 text-gray-400" />
            <span className="text-sm text-gray-600">
              {searchTerm || filters.status !== 'all' || filters.jobType !== 'all' 
                ? 'Filtered results' 
                : 'All jobs'
              }
            </span>
          </div>
        </div>
      </motion.div>

      {/* Jobs Grid */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8"
      >
        {(jobs || []).map((job, index) => (
          <motion.div
            key={job._id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 + index * 0.05 }}
            className="card hover:shadow-hover transition-all duration-300"
          >
            {/* Job Header */}
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 mb-2 line-clamp-2">
                  {job.title}
                </h3>
                <div className="flex items-center text-sm text-gray-600 mb-2">
                  <MapPin className="w-4 h-4 mr-1" />
                  {job.location?.city ? `${job.location.city}, ${job.location.state}` : 'Location not specified'}
                </div>
                <div className="flex items-center text-sm text-gray-600">
                  <DollarSign className="w-4 h-4 mr-1" />
                  {getSalaryRange(job)}
                </div>
              </div>
              <span className={`status-badge ${
                job.status === 'Active' || job.status === 'active' ? 'status-active' :
                job.status === 'Inactive' || job.status === 'inactive' ? 'status-inactive' :
                'status-pending'
              }`}>
                {job.status}
              </span>
            </div>

            {/* Job Details */}
            <div className="mb-4">
              <div className="flex items-center justify-between text-sm text-gray-600 mb-2">
                <span className="flex items-center">
                  <Users className="w-4 h-4 mr-1" />
                  {job.applicationStats?.totalApplications || 0} applications
                </span>
                <span className="flex items-center">
                  <Eye className="w-4 h-4 mr-1" />
                  {job.stats?.views || 0} views
                </span>
              </div>
              
              <div className="flex items-center text-sm text-gray-600">
                <Calendar className="w-4 h-4 mr-1" />
                Posted {getTimeAgo(job.createdAt)}
              </div>
            </div>

            {/* Job Type and Level */}
            <div className="flex flex-wrap gap-2 mb-4">
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                {job.employmentType || 'Full-time'}
              </span>
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                {job.level || 'Entry Level'}
              </span>
            </div>

            {/* Description Preview */}
            <p className="text-sm text-gray-600 mb-4 line-clamp-3">
              {job.description}
            </p>

            {/* Actions */}
            <div className="flex items-center justify-between pt-4 border-t border-gray-200">
              <div className="flex items-center space-x-2">
                <Link
                  to={`/jobs/edit/${job._id}`}
                  className="text-blue-600 hover:text-blue-700 p-2 rounded-lg hover:bg-blue-50 transition-colors"
                  title="Edit Job"
                >
                  <Edit className="w-4 h-4" />
                </Link>
                <button
                  onClick={() => handleDelete(job._id)}
                  className="text-red-600 hover:text-red-700 p-2 rounded-lg hover:bg-red-50 transition-colors"
                  title="Delete Job"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
              
              <button
                onClick={() => handleStatusChange(
                  job._id, 
                  (job.status === 'Active' || job.status === 'active') ? 'Inactive' : 'Active'
                )}
                className={`btn-secondary text-sm py-1 px-3 ${
                  (job.status === 'Active' || job.status === 'active')
                    ? 'text-red-600 hover:bg-red-50' 
                    : 'text-green-600 hover:bg-green-50'
                }`}
              >
                {(job.status === 'Active' || job.status === 'active') ? 'Deactivate' : 'Activate'}
              </button>
            </div>
          </motion.div>
        ))}
      </motion.div>

      {/* Empty State */}
      {(!jobs || jobs.length === 0) && !loading && (
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="empty-state"
        >
          <Briefcase className="empty-state-icon" />
          <h3 className="empty-state-title">
            {searchTerm || filters.status !== 'all' || filters.jobType !== 'all'
              ? 'No jobs found'
              : 'No jobs posted yet'
            }
          </h3>
          <p className="empty-state-description">
            {searchTerm || filters.status !== 'all' || filters.jobType !== 'all'
              ? 'Try adjusting your search criteria or filters'
              : 'Start by posting your first job to attract candidates'
            }
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            {(searchTerm || filters.status !== 'all' || filters.jobType !== 'all') && (
              <button
                onClick={() => {
                  setSearchTerm('');
                  setFilters({
                    status: 'all',
                    location: '',
                    jobType: 'all',
                    sortBy: '-createdAt'
                  });
                }}
                className="btn-secondary"
              >
                Clear Filters
              </button>
            )}
            <Link to="/jobs/new" className="btn-primary">
              Post Your First Job
            </Link>
          </div>
        </motion.div>
      )}

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="pagination"
        >
          <button
            onClick={() => handlePageChange(pagination.currentPage - 1)}
            disabled={pagination.currentPage === 1}
            className="pagination-item disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>
          
          {[...Array(pagination.totalPages)].map((_, index) => {
            const page = index + 1;
            return (
              <button
                key={page}
                onClick={() => handlePageChange(page)}
                className={`pagination-item ${
                  pagination.currentPage === page ? 'active' : ''
                }`}
              >
                {page}
              </button>
            );
          })}
          
          <button
            onClick={() => handlePageChange(pagination.currentPage + 1)}
            disabled={pagination.currentPage === pagination.totalPages}
            className="pagination-item disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </motion.div>
      )}
    </div>
  );
};

export default Jobs; 